<template>
  <div>
    <!-- #823: viewer 진입 시 단계별 깜빡임(이전 보고서 Nav → 일반 메인 → 빈 본문 → 스켈레톤 →
         AI 메인)을 가리기 위한 흰색 페이드 오버레이.
         viewerReady가 true가 되는 순간 leave-transition으로 1s 동안 서서히 사라진다. -->
    <transition name="viewer-fade">
      <div v-if="!viewerReady && not_found === ''" class="viewer-loading-overlay"></div>
    </transition>

    <template v-if="viewerReady && (current_report_code || (selected_report && not_found === ''))">
      <ViewAssuranceStatement v-if="['/a'].includes($route.path)" />
      <FullView2021 v-else-if="$route.path === '/v' || getParsedReportCodeFromUrl" />
      <GRITableView2021 v-else-if="['/g', '/s', '/t'].includes($route.path)" />
    </template>
    <template v-else-if="not_found != ''">
      <div class="vh-100 text-center bgray-200">
        <div style="height: 70vh" class="d-flex justify-content-center align-items-center">
          <h2 class="teal8 pt-5 pb-2 border-bottom-teal7 px-md-5">
            <span v-if="not_found == 'not_published'">아직 공개되지 않은 보고서 입니다.</span>
            <span v-else-if="not_found == 'not_exitst'">존재하지 않는 보고서 입니다.</span>
            <span v-else> 알 수 없는 에러입니다. 시스템 관리자에 문의해 주세요.</span>
          </h2>
        </div>

        <div>
          <b-button variant="icon-teal" href="https://report.qesg.co.kr"
            >ESG Reporting Tool 바로가기 <b-icon icon="link-45deg" class="mr-1" />
          </b-button>
          <b-button
            variant="icon-teal"
            href="https://qesg.co.kr"
            target="_blank"
            class="m-0 mx-sm-3"
            >QESG 바로가기 <b-icon icon="link-45deg" class="mr-1"
          /></b-button>
          <b-button variant="icon-teal" href="https://qesg.co.kr/#i-5" target="_blank"
            >문의하기
            <fa icon="question-circle" class="ml-1" />
          </b-button>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { mapState, mapMutations, mapGetters, mapActions } from 'vuex';
import { read_mixin } from '@/mixins/read_mixin';
import FullView2021 from '@/components/report_preview/gri2021_viewer/FullView2021.vue';
import GRITableView2021 from '@/components/report_preview/gri2021_viewer/GRITableView2021.vue';
import ViewAssuranceStatement from '@/components/report_preview/appendix_subs/ViewAssuranceStatement';

export default {
  name: 'ViewerMaster',
  components: {
    FullView2021,
    GRITableView2021,
    ViewAssuranceStatement
  },
  mixins: [read_mixin],
  data() {
    return {
      not_found: '',
      loadingShow: true,
      originalFontSize: '',
      // #823: setupViewerMode가 모든 fetch를 await로 끝낸 뒤 true로 설정.
      // computed로 theme_option/main_setting/toc_loaded를 직접 보면 API가 code:false를 반환한
      // 케이스에서 영원히 false → 무한 검정 화면이 되므로 데이터-flag 분리.
      viewerForceReady: false,
      // 안전망: 어떤 이유로든 setupViewerMode가 끝나지 않더라도 3초 후 강제 해제.
      viewerReadyTimer: null
    };
  },
  computed: {
    ...mapGetters(['is_viewer']),
    ...mapGetters('report_list', ['selected_report', 'current_report_code']),
    ...mapState('report_list', ['is_all_report_list_loaded', 'viewer_report_code', 'view_mode']),
    ...mapState('story_board', ['is_fetching_lv2_disclosure']),
    ...mapGetters('story_board', [
      'sb_toc',
      'selected_lv1',
      'selected_lv2',
      'selected_viewer_disclosures'
    ]),
    ...mapGetters('assurance', ['has_assurance_content', 'is_assurance_deployed']),
    getParsedReportCodeFromUrl() {
      if (process.env.VUE_APP_REPORT_CODE) {
        return process.env.VUE_APP_REPORT_CODE;
      } else {
        const queryReportCode = this.$route.query.r;
        return queryReportCode ? queryReportCode.split('?')[0] : '';
      }
    },
    // #823: viewer 모드에서 자식(FullView2021 등) 렌더를 막아 단계별 깜빡임을 차단하는 게이트.
    // editor 모드 / not_found 분기는 즉시 통과(기존 UX 영향 없음).
    viewerReady() {
      if (!this.is_viewer) return true;
      if (this.not_found !== '') return true;
      return this.viewerForceReady;
    }
  },
  methods: {
    ...mapActions('story_board', [
      'fetch_sb_toc',
      'fetch_main_setting_from_viewer',
      'fetch_viewer_sb_disclosures'
    ]),
    ...mapMutations('story_board', ['update_selected_lv1', 'update_selected_lv2']),
    ...mapMutations('report_list', [
      'update_viewer_report_code',
      'update_all_report_list_loaded',
      'update_view_mode',
      'change_lang'
    ]),
    decodeKey(key) {
      return key
        .replace(/%20/g, ' ')
        .replace(/%0A/g, '\n')
        .replace('+', ' ')
        .replace('\n', '')
        .trim();
    },

    handleRouteChange(to) {
      try {
        const { lv1: lv1Key, lv2: lv2Key, l: lang } = to.query;
        if (lang) {
          if (lang === 'ENG') {
            this.change_lang('ENG');
          } else {
            this.change_lang('KOR');
          }
        } else {
          this.change_lang('KOR');
        }

        if (!this.sb_toc || this.sb_toc.length === 0) return;

        const decodeKey = this.decodeKey;

        if (lv1Key) {
          const lv1_info = this.sb_toc.find(
            item =>
              decodeKey(item.name) === lv1Key.trim().replace('\n', '') || item.key === lv1Key.trim()
          );

          if (lv1_info) {
            if (lv1_info.key !== this.selected_lv1?.key) {
              this.update_selected_lv1({
                lv1_info,
                view_mode: this.is_viewer ? 'viewer' : 'editor'
              });
            }

            if (lv2Key) {
              const lv2_info = lv1_info.children?.find(
                item =>
                  decodeKey(item.name) === lv2Key.trim().replace('\n', '') ||
                  item.key === lv2Key.trim()
              );

              if (lv2_info && lv2_info.key !== this.selected_lv2?.key) {
                this.update_selected_lv2({
                  lv2_info,
                  view_mode: this.is_viewer ? 'viewer' : 'editor'
                });
              }

              if (!this.is_fetching_lv2_disclosure && !this.selected_viewer_disclosures) {
                this.fetch_viewer_sb_disclosures({
                  report_code: this.current_report_code,
                  selected_lv_info: {
                    lv1_info,
                    lv2_info
                  }
                });
              }
            }
          }
        } else {
          this.update_selected_lv1({
            lv1_info: null,
            view_mode: this.is_viewer ? 'viewer' : 'editor'
          });
          this.update_selected_lv2({
            lv2_info: null,
            view_mode: this.is_viewer ? 'viewer' : 'editor'
          });
        }
      } catch (error) {
        console.error('[handleRouteChange error]', error);
      }
    },

    async handleReportStatus(parsed_report_code) {
      await this.readAll(parsed_report_code);
      // readAssurance is now called inside readAll for all routes

      this.$nextTick(() => {
        if (parsed_report_code && this.is_all_report_list_loaded) {
          if (this.selected_report) {
            if (this.selected_report.state === 4) {
              // this.update_viewer_report_code(this.selected_report.report_code);
            } else {
              this.not_found = 'not_published';
              console.warn('아직 공개되지 않은 보고서 입니다.');
            }
          } else {
            this.not_found = 'not_exitst';
            console.warn('존재하지 않는 보고서 입니다.');
          }
        } else {
          this.not_found = 'donno';
        }
      });
    },

    async setupViewerMode() {
      const parsed_report_code = this.getParsedReportCodeFromUrl;
      try {
        if (parsed_report_code) {
          await this.handleReportStatus(parsed_report_code);
          // #823: viewerReady 게이트(=자식 마운트 시점)에 필요한 storyboard 데이터를 미리 fetch.
          // 자식이 mount된 뒤 watch에서 fetch하면 main_setting/sb_toc 로딩 동안 단계별 깜빡임 발생.
          const reportCode = this.current_report_code;
          if (reportCode && this.$route.path === '/v' && this.not_found === '') {
            await Promise.all([
              this.fetch_main_setting_from_viewer({ report_code: reportCode }),
              this.fetch_sb_toc({ report_code: reportCode, select_lv: false })
            ]);
          }
        }
      } catch (e) {
        console.warn('[ViewerMaster setupViewerMode]', e);
      } finally {
        // 어떤 fetch가 실패해도 화면은 풀어준다(무한 검정 방지).
        this.viewerForceReady = true;
        if (this.viewerReadyTimer) {
          clearTimeout(this.viewerReadyTimer);
          this.viewerReadyTimer = null;
        }
      }

      // if (this.$route.query.l === 'ENG') {
      //   this.change_lang('ENG');
      // }
    },

    // is_indexable === false 인 보고서에 한해 noindex 메타 태그를 주입한다.
    // true 또는 undefined(구버전 응답 호환)일 때는 주입하지 않는다.
    // SPA 특성상 100% 보장되진 않으나, 대부분의 크롤러는 JS 렌더링 후 meta robots를 인식한다.
    applyRobotsMeta(report) {
      const shouldBlock = report?.is_indexable === false;
      let metaTag = document.querySelector('meta[name="robots"]');

      if (shouldBlock) {
        if (!metaTag) {
          metaTag = document.createElement('meta');
          metaTag.setAttribute('name', 'robots');
          document.head.appendChild(metaTag);
        }
        metaTag.setAttribute('content', 'noindex, nofollow');
        metaTag.dataset.injectedByViewer = 'true';
      } else if (metaTag && metaTag.dataset.injectedByViewer === 'true') {
        metaTag.parentNode.removeChild(metaTag);
      }
    }
  },
  watch: {
    sb_toc: {
      immediate: true,
      handler() {
        this.handleRouteChange(this.$route);
      }
    },
    $route: {
      immediate: true,
      handler(to, from) {
        this.handleRouteChange(to);
        // #823: SPA 내부에서 뷰어 보고서 간 이동 시(/v?r=A → /v?r=B 또는 /v ↔ /g/s/t/a)
        // 컴포넌트가 재마운트되지 않아 setupViewerMode가 다시 안 돌면 오버레이/persisted state
        // 초기화가 누락된다. from이 존재하고(=초기 immediate 아님) URL 보고서가 바뀐 경우 재실행.
        if (!from || !this.is_viewer) return;
        const reportChanged = to.query?.r !== from.query?.r;
        const pathChanged = to.path !== from.path;
        if (!reportChanged && !pathChanged) return;

        this.viewerForceReady = false;
        if (this.viewerReadyTimer) clearTimeout(this.viewerReadyTimer);
        this.viewerReadyTimer = setTimeout(() => {
          this.viewerForceReady = true;
          this.viewerReadyTimer = null;
        }, 3000);
        // created()의 stale-clear와 동일 효과
        if (!process.env.VUE_APP_REPORT_CODE) {
          const url_report_url = this.getParsedReportCodeFromUrl;
          const persisted_report_url = this.selected_report?.report_url;
          if (url_report_url && persisted_report_url && url_report_url !== persisted_report_url) {
            this.update_viewer_report_code('');
          }
        }
        this.setupViewerMode();
      }
    },
    selected_report(newVal) {
      // newVal이 undefined이거나 null일 경우를 체크
      if (!newVal || !newVal.report_code) return;

      if (this.current_report_code && newVal.report_code === this.current_report_code) {
        document.querySelector('title').textContent = newVal.report_name;
        document.documentElement.style.setProperty('--main-color', newVal.main_color);

        this.originalFontSize =
          document.documentElement.style.fontSize ||
          window.getComputedStyle(document.documentElement).fontSize;

        if (newVal.font_size) {
          document.documentElement.style.fontSize = `${newVal.font_size}px`;
        }

        this.applyRobotsMeta(newVal);
      }
    }
  },
  created() {
    // #823: persisted viewer 상태가 현재 URL의 보고서와 다르면 첫 렌더 전에 비워서
    // 이전 보고서의 Nav가 잠깐 깜빡이는 문제를 방지한다.
    // mounted()는 자식 렌더 이후라 늦으므로 created()에서 동기로 처리.
    if (this.is_viewer && !process.env.VUE_APP_REPORT_CODE) {
      const url_report_url = this.getParsedReportCodeFromUrl;
      const persisted_report_url = this.selected_report?.report_url;
      if (url_report_url && persisted_report_url && url_report_url !== persisted_report_url) {
        this.update_viewer_report_code('');
      }
    }
  },
  mounted() {
    this.update_view_mode('viewer');

    if (this.is_viewer) {
      // 안전망: 어떤 이유로든 setupViewerMode가 3초 안에 ready 신호를 못 주면 강제로 풀어준다.
      this.viewerReadyTimer = setTimeout(() => {
        this.viewerForceReady = true;
        this.viewerReadyTimer = null;
      }, 3000);
      this.setupViewerMode();
    } else {
      this.update_view_mode('editor');
      this.viewerForceReady = true;
    }
  },
  beforeDestroy() {
    document.documentElement.style.fontSize = this.originalFontSize;

    if (this.viewerReadyTimer) {
      clearTimeout(this.viewerReadyTimer);
      this.viewerReadyTimer = null;
    }

    const metaTag = document.querySelector('meta[name="robots"]');
    if (metaTag && metaTag.dataset.injectedByViewer === 'true') {
      metaTag.parentNode.removeChild(metaTag);
    }
  }
};
</script>
<style lang="scss" scoped>
// #823: 뷰어 진입 시 단계별 깜빡임을 가리는 흰색 페이드 오버레이.
// viewerReady가 true가 되면 v-if가 풀리며 leave-transition으로 1s 동안 서서히 사라진다.
.viewer-loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 9999;
  // 페이지 로드 직후 브라우저 기본 흰색과 동일해 첫 페인트가 매끄럽게 이어진다.
  // 페이드 아웃되며 흰색 → 보고서 본문 색감으로 자연스럽게 어두워지는 인상을 준다.
  background-color: #ffffff;
  pointer-events: none;
}

.viewer-fade-leave-active {
  transition: opacity 1s ease-in;
}
.viewer-fade-leave-to {
  opacity: 0;
}
</style>
